// Must be first.
import 'reflect-metadata';

// Polyfills.
import '@/utils/eventBus';

// Import the base styles.
import './styles/zo-ui-base.scss';

import { initLongTaskBuffer, onLongTask } from './utils/longTaskBuffer';
import { METRIC_EVENTS, METRIC_THRESHOLDS, METRIC_INTERVALS } from './utils/metricsConstants';
import { initNetworkDiagnostics } from './utils/networkDiagnostics';
import { initPerfTelemetry } from './utils/perfTelemetry';
import { initSessionMonitor } from './utils/sessionMonitor';

import { container } from '@/container';
import { AppController } from '@/controllers/AppController';
import { Connection } from '@/services/Connection';
import { Environment } from '@/services/Environment';
import { Logger } from '@/services/Logger';
import { Toaster } from '@/services/toaster/Toaster';
import { getPerfContext, setPerfContext, withPerfSpan } from '@/utils/perfContext';
import { startCase } from '@/utils/startCase';

/**
 * PERF + LOGROCKET TOGGLES
 *
 * Prod behavior:
 * - LogRocket is enabled
 * - Performance observers are enabled
 * - Perf events are sent to LogRocket
 *
 * Dev behavior (opt-in):
 * - LogRocket is OFF by default (to avoid noise) unless enabled via:
 *    - URL: ?logrocket=1
 *    - localStorage: localStorage.setItem('logrocket', '1')
 * - Perf observers are OFF by default unless enabled via:
 *    - URL: ?perf=1
 *    - localStorage: localStorage.setItem('perf', '1')
 *
 * In dev:
 * - If perf is enabled but LogRocket is not, we log perf events to console.debug
 * - If both perf + LogRocket are enabled, we send perf events to LogRocket
 */

const IS_PROD = import.meta.env.PROD;

function hasFlag(name: string): boolean {
  if (IS_PROD) return true;

  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get(name) === '1') return true;

    return window.localStorage.getItem(name) === '1';
  }
  catch {
    //  If localStorage is unavailable, treat as disabled.
    return false;
  }
}

const LOGROCKET_ENABLED = hasFlag('logrocket');
const PERF_ENABLED = hasFlag('perf');
const PERF_DEBUG = hasFlag('perfdebug');

//  Performance Mark: app start. used to measure initial-ui performance once the app finsihes booting
performance.mark('app-start');

//  Force Logger/LogRocket to initialize as early as possible (before boot)
const logger = LOGROCKET_ENABLED ? container.get(Logger) : undefined;

const pv = (globalThis as unknown as { PACKAGE_VERSION?: unknown }).PACKAGE_VERSION;

const release: string = typeof pv === 'string' ? pv : 'unknown';

const perf = initPerfTelemetry({
  logger,
  enabled: PERF_ENABLED,
  sendToLogRocket: LOGROCKET_ENABLED,
  env: IS_PROD ? 'prod' : 'dev',
  release,
  flushEveryMs: METRIC_INTERVALS.PERF_TELEMETRY_FLUSH_MS,
  maxSamplesPerMetric: 200,
  debug: PERF_DEBUG,
});

if (!IS_PROD) {
  //  eslint-disable-next-line no-underscore-dangle
  (window as Window & { _perf?: typeof perf })._perf = perf;
}

/**
 * Long Task tracking:
 * - Uses the shared longTaskBuffer (single PerformanceObserver for all consumers).
 * - Attaches current UI context from perfContext.ts so we know what the user was doing when the slowness occurred.
 *
 * In prod, sends to LogRocket.
 * In dev, sends to LogRocket only if enabled; otherwise logs to console.
 */
function setupLongTaskTracking(): void {
  if (!PERF_ENABLED) return;

  //  Initialize the shared buffer (safe to call multiple times)
  initLongTaskBuffer();

  const THRESHOLD_MS = METRIC_THRESHOLDS.LONG_TASK_THRESHOLD_MS;

  //  Register a listener for real-time LogRocket emission
  onLongTask((entry) => {
    if (entry.duration >= THRESHOLD_MS) {
      const { label, ageMs } = getPerfContext();

      const payload = {
        duration: Math.round(entry.duration),
        uiContext: label,
        uiContextAgeMs: typeof ageMs === 'number' ? Math.round(ageMs) : undefined,
      };

      if (LOGROCKET_ENABLED) {
        logger?.track?.(METRIC_EVENTS.LONG_TASK, payload);
      }
      else {
        //  eslint-disable-next-line no-console
        console.debug('[perf] Long Task', payload);
      }
    }
  });
}

//  Start observing ASAP after logger is initilaized.
setupLongTaskTracking();

//  Network diagnostics: probes, connection API data, API timing percentiles, likely-cause analysis.
initNetworkDiagnostics({
  logger,
  enabled: PERF_ENABLED,
  sampleEveryMs: METRIC_INTERVALS.NETWORK_DIAGNOSTICS_MS,
  debug: PERF_DEBUG,
});

//  Session monitor: degradation, error density, throughput, composite health (every 10 min).
//  Staggered by 2 minutes to avoid burst emission at same time as network diagnostics.
initSessionMonitor({
  logger,
  enabled: PERF_ENABLED,
  intervalMs: METRIC_INTERVALS.SESSION_MONITOR_MS,
  startDelayMs: 2 * 60 * 1000,
  debug: PERF_DEBUG,
});

/**
 * Initial UI measure:
 * - Marks app as "ready" and measure from app-start -> app-ready
 * - Sends "initial UI ready" to LogRocket when it's enabled
 * - Switches baseline perf context to "idle" after boot
 *
 * Setting to Idle gives clean separation in long-task. Specific operations override context.
 */
function markAndTrackInitialUiReady(): void {
  // Mark + measure
  performance.mark('app-ready');
  performance.measure('initial-ui', 'app-start', 'app-ready');
  performance.measure('span:ui:app:initial-ui', 'app-start', 'app-ready');

  //  Get last measure entry
  const entries = performance.getEntriesByName('initial-ui', 'measure') as PerformanceMeasure[];
  const m = entries[entries.length - 1];

  if (LOGROCKET_ENABLED && m) {
    container.get(Logger).track?.('Initial UI Ready', { duration: m.duration });
  }

  //  After initial boot, default context is "idle".
  setPerfContext('Idle');
}

(async () => {
  try {
    // Initialize the environment.
    await withPerfSpan(
      'ui:startup:environment.initialize',
      () => container.get(Environment).initialize(),
      {},
      { always: PERF_DEBUG, mark: true },
    );

    // Initialize the connection.
    await withPerfSpan(
      'ui:startup:connection.initialize',
      () => container.get(Connection).initialize(),
      {},
      { always: PERF_DEBUG, mark: true },
    );

    // Initialize the application.
    await withPerfSpan(
      'ui:startup:appcontroller.initialize',
      () => container.get(AppController).initialize(),
      {},
      { always: PERF_DEBUG, mark: true },
    );
  }
  catch (e) {
    const error = e as Error;

    // Only log exceptions using LogRocket when LogRocket is enabled.
    if (LOGROCKET_ENABLED) {
      container.get(Logger).captureException(error, { tags: { unhandledException: true } });
    }

    // Replace the context with the error name.
    const toaster = await container.getAsync(Toaster);
    toaster.error(error, { context: startCase(error.name) });

    // eslint-disable-next-line no-console
    console.error(error);
  }
  finally {
    //  always measure (success or failure)
    markAndTrackInitialUiReady();
  }
})().catch(() => {
  //  This is here to satisfy the no-floating-promises + no-void.
  // The error is already handled in teh try/catch above.
});
